import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { Card, Badge, Button, ProgressBar } from '@/shared/ui';
import { extractionApi } from '../api/extractionApi';
import { strategyKeys } from '../queryKeys';
import { WIZARD_STEPS } from '../components/StepNavBar';
import { VapiCallModal } from '../components/VapiCallModal';
import { VapiCallBanner } from '../components/VapiCallBanner';
import type { LifecycleData } from '../api/lifecycleApi';
import styles from './StrategyOverviewPage.module.css';

interface Props {
  lifecycle: LifecycleData;
  onResume: () => void;
  onNavigateStep: (step: number) => void;
  locationId: string;
  locationPhone?: string;
  autoOpenModal?: boolean;
}

export function InProgressView({
  lifecycle,
  onResume,
  onNavigateStep,
  locationId,
  locationPhone,
  autoOpenModal,
}: Props) {
  const [vapiModalOpen, setVapiModalOpen] = useState(false);

  useEffect(() => {
    if (autoOpenModal) setVapiModalOpen(true);
  }, [autoOpenModal]);

  const { data: extractionData } = useQuery({
    queryKey: strategyKeys.extraction(locationId),
    queryFn: () => extractionApi.get(locationId),
    enabled: !!locationId,
  });
  const confirmedSections = extractionData?.data?.confirmedSections ?? [];
  const allProfileSectionsConfirmed = ['growth', 'patients', 'budget', 'brand'].every((k) =>
    confirmedSections.includes(k),
  );

  // The saved step is only the furthest step reached — clicking Next past the
  // profile doesn't confirm it. Step 1 counts as done once it is confirmed.
  const profileConfirmed =
    allProfileSectionsConfirmed && extractionData?.data?.status === 'confirmed';

  const step = lifecycle.strategy?.currentStep ?? 1;
  const stepLabel = WIZARD_STEPS.find((s) => s.id === step)?.label ?? 'Practice Profile';
  const totalSteps = WIZARD_STEPS.filter((s) => s.id > 0).length;
  const percent = Math.round(((step - 1) / totalSteps) * 100);
  const startDate = lifecycle.strategy?.createdAt
    ? new Date(lifecycle.strategy.createdAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;
  const { enrichment } = lifecycle;

  const dataSummary = [
    {
      label: 'Practice Profile',
      done: profileConfirmed,
      detail: profileConfirmed ? 'Confirmed' : step > 1 ? 'Not fully confirmed yet' : 'Not started',
    },
    {
      label: 'Practice Data',
      done: step > 2,
      detail: enrichment?.uploadCount
        ? `${enrichment.uploadCount}/4 uploads (${enrichment.level})`
        : step > 2
          ? 'Skipped — no uploads'
          : 'Not started',
    },
    {
      label: 'Market Intelligence',
      done: step > 3,
      detail: step > 3 ? 'Reviewed' : 'Not started',
    },
    {
      label: 'Competition',
      done: step > 4,
      detail: step > 4 ? 'Reviewed' : 'Not started',
    },
  ];

  return (
    <div className={styles.wrapper}>
      <div className={styles.inProgressHeader}>
        <h2 className={styles.pageTitle}>Practice Strategy</h2>
        <div className={styles.badgeRow}>
          <Badge variant="warning">In Progress</Badge>
          {startDate && <span className={styles.dateMeta}>Started: {startDate}</span>}
        </div>
      </div>

      <VapiCallBanner
        locationId={locationId}
        onOpen={() => setVapiModalOpen(true)}
        suppressPartial={allProfileSectionsConfirmed}
      />
      <VapiCallModal
        isOpen={vapiModalOpen}
        onClose={() => setVapiModalOpen(false)}
        locationId={locationId}
        defaultPhone={locationPhone}
      />

      <Card className={styles.glowCard}>
        <div className={styles.resumeCardHeader}>
          <div>
            <div className={styles.resumeCardTitle}>Resume where you left off</div>
            <div className={styles.resumeCardSub}>
              Step {step}: {stepLabel}
            </div>
          </div>
          <Button variant="primary" onClick={onResume}>
            Resume →
          </Button>
        </div>
        <div className={styles.progressRow}>
          <div className={styles.progressBarFlex}>
            <ProgressBar value={percent} />
          </div>
          <span className={styles.percentLabel}>{percent}%</span>
        </div>
      </Card>

      <Card className={styles.cardMbMd}>
        <div className={styles.cardSectionTitle}>Step Progress</div>
        <div className={styles.stepList}>
          {WIZARD_STEPS.filter((s) => s.id > 0).map((ws) => {
            const passed = ws.id < step;
            // Passed but not actually finished (only the profile can be).
            const needsWork = passed && ws.id === 1 && !profileConfirmed;
            const isComplete = passed && !needsWork;
            const isCurrent = ws.id === step;
            const isFuture = ws.id > step;
            const canClick = passed || isCurrent;

            return (
              <div
                key={ws.id}
                onClick={
                  canClick ? () => (isCurrent ? onResume() : onNavigateStep(ws.id)) : undefined
                }
                className={[
                  styles.stepRow,
                  isCurrent ? styles.stepRowCurrent : '',
                  !canClick ? styles.stepRowNoClick : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span
                  className={[
                    styles.stepStatusIcon,
                    isComplete
                      ? styles.stepStatusIconComplete
                      : isCurrent || needsWork
                        ? styles.stepStatusIconCurrent
                        : styles.stepStatusIconFuture,
                  ].join(' ')}
                >
                  {isComplete ? <Check size={12} aria-hidden /> : ws.id}
                </span>

                <span
                  className={[
                    styles.stepLabel,
                    isCurrent ? styles.stepLabelCurrent : isFuture ? styles.stepLabelFuture : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {ws.label}
                </span>

                {(passed || isCurrent) && (
                  <span
                    className={[
                      styles.stepText,
                      isComplete ? styles.stepTextComplete : styles.stepTextCurrent,
                    ].join(' ')}
                  >
                    {needsWork
                      ? 'Needs confirming →'
                      : isComplete
                        ? ws.id === 1
                          ? 'Confirmed'
                          : 'Reviewed'
                        : 'In progress →'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card>
        <div className={styles.cardSectionTitle}>What we have so far</div>
        <div className={styles.dataSummaryGrid}>
          {dataSummary.map((d, i) => (
            <div key={i} className={styles.dataSummaryCell}>
              <div className={styles.dataSummaryLabelRow}>
                <span
                  className={[
                    styles.summaryIconBase,
                    d.done ? styles.summaryIconDone : styles.summaryIconPending,
                  ].join(' ')}
                >
                  {d.done ? <Check size={10} aria-hidden /> : '–'}
                </span>
                <span className={styles.dataSummaryLabel}>{d.label}</span>
              </div>
              <div
                className={[
                  styles.summaryDetail,
                  d.done ? styles.summaryDetailDone : styles.summaryDetailPending,
                ].join(' ')}
              >
                {d.detail}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
